import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPKR, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, ShieldCheck, Users, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import PageTip from "../components/PageTip";

function initials(name = "") {
  return name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "?";
}

export default function Guarantors() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const load = (query = "") => {
    setLoading(true);
    api.get("/guarantors", { params: query ? { q: query, limit: 100 } : { limit: 100 } })
      .then((r) => setRows(r.data || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const backfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/guarantors/backfill");
      toast.success(`Backfill complete — ${data.imported} new guarantor(s) imported.`);
      load(q.trim());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBackfilling(false); }
  };

  return (
    <div className="space-y-5 page-narrow" data-testid="guarantors-page">
      <PageTip>
        <ShieldCheck className="w-4 h-4 inline mr-1.5" />
        Central guarantor pool — har customer ke guarantors yahan se centrally manage hote hain. Repeat guarantor add karte waqt CNIC/mobile pe duplicate prompt aata hai.
      </PageTip>

      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="font-display text-2xl font-bold text-slate-900 flex-1">Guarantors</h1>
        <Button onClick={backfill} disabled={backfilling} variant="outline" size="sm" data-testid="guarantors-backfill-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${backfilling ? "animate-spin" : ""}`} />
          {backfilling ? "Importing…" : "Re-scan existing customers"}
        </Button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, mobile, ya CNIC…"
          className="pl-9"
          data-testid="guarantors-search"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {loading && <div className="px-4 py-8 text-center text-slate-500 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="px-4 py-10 text-center text-slate-500 text-sm">
            <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            {q ? <>No guarantor matched <span className="font-semibold">"{q}"</span>.</> : "Abhi tak koi guarantor record nahi hai. Existing customers se import karne ke liye 'Re-scan' button click karein."}
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">Guarantor</th>
                  <th className="px-4 py-2 text-left">CNIC</th>
                  <th className="px-4 py-2 text-left">Mobile</th>
                  <th className="px-4 py-2 text-left">City</th>
                  <th className="px-4 py-2 text-right">Customers</th>
                  <th className="px-4 py-2 text-right">Active</th>
                  <th className="px-4 py-2 text-right">Outstanding</th>
                  <th className="px-4 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => (
                  <tr key={g.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`guarantor-row-${g.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center text-slate-500 font-bold text-xs flex-shrink-0">
                          {g.documents?.photo ? (
                            <img src={g.documents.photo} alt={g.name} className="w-full h-full object-cover" />
                          ) : initials(g.name)}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{g.name}</div>
                          {g.relationship && <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">{g.relationship}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular text-slate-600 text-xs">{g.cnic || "—"}</td>
                    <td className="px-4 py-3 tabular text-slate-600 text-xs">{g.mobile || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{g.city || "—"}</td>
                    <td className="px-4 py-3 text-right tabular text-sky-700 font-bold">{g.customers_linked || 0}</td>
                    <td className="px-4 py-3 text-right tabular text-emerald-700 font-bold">{g.active_loans || 0}</td>
                    <td className="px-4 py-3 text-right tabular text-amber-700">{g.outstanding_amount > 0 ? fmtPKR(g.outstanding_amount) : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/guarantors/${g.id}`} className="text-emerald-700 hover:text-emerald-900 font-semibold text-xs" data-testid={`guarantor-view-${g.id}`}>
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
